import { useContext, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { type AxiosError } from "axios";
import { PageContainer } from "@packsize/components";
import { useToast } from "@packsize/toast";
import { Grid, styled } from "@mui/material";
import { type z } from "zod";

import { SmallBook } from "@/components/typography";
import {
	isScanToTriggerConfigured,
	isScanToTriggerSingleConfigured,
} from "@/views/configuration/production-groups/manual-scan-to-produce-dropdown";
import { ErrorModal } from "@/components/modals";
import {
	deleteBatchById,
	produceNext,
	produceNow,
	triggerJobByBatchId,
	triggerJobByLpnWithBody,
} from "@/api/selection-api";
import { type selectionValidationErrorSchema } from "@/schemas/routing/selection-validation";
import { ProductionContext } from "@/contexts/production";

import { InProductionOrdersCard } from "./in-production-orders-card/in-production-orders-card";
import { availableBatchesQueryKey } from "./query/use-available-batches";
import { AvailableBatchesCard } from "./available-batches/";
import { OrdersContext } from "./orders.context";
import { getErrorMessage } from "./error-utils";

interface ErrorModalData {
	title: string;
	message: string;
}

export const DarkGreyText = styled(SmallBook)`
	color: ${({ theme }) => theme.palette.text.secondary};
`;

export const Orders = () => {
	const intl = useIntl();
	const { toastSuccess, toastError, toastInfo } = useToast();
	const { currentProductionGroup, currentMachineGroup } = useContext(ProductionContext);
	const machineGroupId = currentMachineGroup?.id || "";

	const scanToTriggerConfigured = isScanToTriggerConfigured(currentProductionGroup?.manualScanToProduce);
	const scanToTriggerSingleConfigured = isScanToTriggerSingleConfigured(currentProductionGroup?.manualScanToProduce);

	const queryClient = useQueryClient();
	const [errorModalData, setErrorModalData] = useState<ErrorModalData | null>(null);

	const { mutate: triggerJobByLPN } = useMutation({
		mutationFn: ({ lpn }: { lpn: string }) => {
			return triggerJobByLpnWithBody(machineGroupId, lpn);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: availableBatchesQueryKey,
			});
			toastSuccess({
				message: intl.formatMessage({ id: "Successfully Triggered Job", defaultMessage: "Successfully Triggered Job" }),
			});
		},
		onError: (error, variables) => {
			console.error("Failed to trigger job", error);
			const axiosError = error as AxiosError;

			if (axiosError.response?.status === 404) {
				toastError({
					message: intl.formatMessage(
						{
							id: "{scanCode} Not Found",
							defaultMessage: "{scanCode} Not Found",
						},
						{ scanCode: variables.lpn },
					),
				});
			} else {
				toastError({
					message: intl.formatMessage({ id: "Failed To Trigger Job", defaultMessage: "Failed to Trigger Job" }),
				});
			}
		},
	});

	const { mutate: removeBatch } = useMutation({
		mutationFn: ({ batchId }: { batchId: string }) => {
			return deleteBatchById(batchId);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: availableBatchesQueryKey,
			});
			toastSuccess({
				message: intl.formatMessage({ id: "Job Canceled Successfully", defaultMessage: "Job Canceled Successfully" }),
			});
		},
		onError: (error) => {
			console.error("Failed to remove job", error);
			toastError({
				message: intl.formatMessage({ id: "Job Cancellation Failed", defaultMessage: "Job Cancellation Failed" }),
			});
		},
	});

	const { mutate: produceNextBatch } = useMutation({
		mutationFn: ({ batchId }: { batchId: string; alias: string | undefined }) => {
			return produceNext(currentMachineGroup?.id || "", batchId);
		},
		onSuccess: () => {
			toastSuccess({ message: intl.formatMessage({ id: "Producing Next", defaultMessage: "Producing Next" }) });
		},
		onError: (error, variables) => {
			console.error("Failed to produce next", error);
			const alias = variables?.alias || "Batch";
			const axiosError = error as AxiosError<z.infer<typeof selectionValidationErrorSchema>>;
			const message = getErrorMessage(axiosError.response?.data, alias, intl);

			setErrorModalData({
				title: intl.formatMessage({ id: "Unable to Produce Next", defaultMessage: "Unable to Produce Next" }),
				message,
			});
		},
	});

	const { mutate: produceNowBatch } = useMutation({
		mutationFn: ({ batchId }: { batchId: string; alias: string | undefined }) => {
			return produceNow(currentMachineGroup?.id || "", batchId);
		},
		onSuccess: () => {
			toastSuccess({ message: intl.formatMessage({ id: "Producing Now", defaultMessage: "Producing Now" }) });
		},
		onError: (error, variables) => {
			console.error("Failed to produce now", error);
			const alias = variables?.alias || "Batch";
			const axiosError = error as AxiosError<z.infer<typeof selectionValidationErrorSchema>>;
			const message = getErrorMessage(axiosError.response?.data, alias, intl);

			setErrorModalData({
				title: intl.formatMessage({ id: "Unable to Produce Now", defaultMessage: "Unable to Produce Now" }),
				message,
			});
		},
	});

	const { mutate: triggerSingleJob } = useMutation({
		mutationFn: ({ batchId }: { batchId: string }) => {
			return triggerJobByBatchId(currentMachineGroup?.id || "", batchId);
		},
		onSuccess: (response) => {
			if (response.status === 200) {
				toastSuccess({
					message: intl.formatMessage({
						id: "Successfully Triggered Job",
						defaultMessage: "Successfully Triggered Job",
					}),
				});
			} else {
				toastInfo({
					message: intl.formatMessage({
						id: "No Jobs Remaining to Trigger",
						defaultMessage: "No Jobs Remaining to Trigger",
					}),
				});
			}
		},
		onError: (error) => {
			console.error("Failed to trigger job", error);
			toastError({
				message: intl.formatMessage({ id: "Failed To Trigger Job", defaultMessage: "Failed to Trigger Job" }),
			});
		},
	});
	const ordersContextValue = useMemo(
		() => ({
			scanToTriggerConfigured,
			scanToTriggerSingleConfigured,
			triggerJobByLPN: (_: unknown, lpn: string) => triggerJobByLPN({ lpn }),
			triggerSingleJob: (batchId: string) => triggerSingleJob({ batchId }),
			removeJob: (batchId: string) => removeBatch({ batchId }),
			produceNext: ({ batchId, alias }: { batchId: string; alias?: string }) => produceNextBatch({ batchId, alias }),
			produceNow: ({ batchId, alias }: { batchId: string; alias?: string }) => produceNowBatch({ batchId, alias }),
		}),
		[
			produceNextBatch,
			produceNowBatch,
			removeBatch,
			scanToTriggerConfigured,
			scanToTriggerSingleConfigured,
			triggerJobByLPN,
			triggerSingleJob,
		],
	);

	return (
		<OrdersContext.Provider value={ordersContextValue}>
			<ErrorModal
				open={Boolean(errorModalData)}
				onClose={() => setErrorModalData(null)}
				title={errorModalData?.title ?? ""}
				message={errorModalData?.message ?? ""}
			/>
			<PageContainer sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
				<Grid display="grid" gridTemplateRows="minmax(280px, auto) minmax(280px, 1fr)" gap={2} sx={{ flexGrow: 1 }}>
					<InProductionOrdersCard />
					<AvailableBatchesCard />
				</Grid>
			</PageContainer>
		</OrdersContext.Provider>
	);
};

export default Orders;
